import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface User {
  id: string;
  email: string;
  name: string;
  cpf?: string;
  phone?: string;
  birthDate?: string;
}

interface TokenResponse {
  access_token: string;
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;
  estruturas?: StructureInfo[];
  idEstruturaPortalAgencia?: number;
  sucesso?: boolean;
  mensagem?: string;
}

interface StructureInfo {
  idEstrutura?: number;
  favorito?: number;
}

interface TokenErrorResponse {
  sucesso?: boolean;
  mensagem?: string;
}

interface CrmAutoSignupPayload {
  nome: string;
  email: string;
  telefone: string;
}

interface CrmAutoSignupResponse {
  sucesso?: boolean;
  mensagem?: string;
}

interface PasswordResetRequestResponse {
  sucesso?: boolean;
  mensagem?: string;
}

interface CreatePasswordWithTokenPayload {
  chave: string;
  senha: string;
  senhaConfirmacao: string;
}

interface CreatePasswordWithTokenResponse {
  sucesso?: boolean;
  mensagem?: string;
}

interface UserProfileResponse extends Record<string, unknown> {
  dados?: Record<string, unknown>;
  data?: Record<string, unknown>;
  usuario?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const STRUCTURE_ID_KEY = 'auth_structure_id';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private refreshInFlight: Promise<string | null> | null = null;
  private userProfileInFlight: Promise<User | null> | null = null;

  private readonly isAuthenticatedSignal = signal<boolean>(false);
  private readonly currentUserSignal = signal<User | null>(null);
  private readonly showTourSignal = signal<boolean>(false);

  readonly isAuthenticated = this.isAuthenticatedSignal.asReadonly();
  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly showTour = this.showTourSignal.asReadonly();

  async requestCrmAutoSignup(payload: CrmAutoSignupPayload): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<CrmAutoSignupResponse>(
          `${environment.apiUrl}/token/auto-cadastro-crm`,
          payload,
        ),
      );

      if (response?.sucesso === false) {
        throw new Error(response.mensagem ?? 'Não foi possível concluir o cadastro.');
      }
    } catch (error: unknown) {
      throw new Error(
        this.extractApiErrorMessage(error) ?? 'Não foi possível concluir o cadastro.',
      );
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    const normalizedEmail = this.normalizeEmail(email);

    try {
      const response = await firstValueFrom(
        this.http.post<PasswordResetRequestResponse>(
          `${environment.apiUrl}/token/solicitar-renovacao-senha-crm`,
          null,
          {
            params: { email: normalizedEmail },
          },
        ),
      );

      if (response?.sucesso === false) {
        throw new Error(response.mensagem ?? 'Não foi possível solicitar a recuperação de senha.');
      }
    } catch (error: unknown) {
      throw new Error(
        this.extractApiErrorMessage(error) ?? 'Não foi possível solicitar a recuperação de senha.',
      );
    }
  }

  async createPasswordWithToken(payload: CreatePasswordWithTokenPayload): Promise<void> {
    const normalizedPayload: CreatePasswordWithTokenPayload = {
      chave: this.normalizeTokenKey(payload.chave),
      senha: this.normalizePassword(payload.senha),
      senhaConfirmacao: this.normalizePassword(payload.senhaConfirmacao),
    };

    try {
      const response = await firstValueFrom(
        this.http.post<CreatePasswordWithTokenResponse>(
          `${environment.apiUrl}/token/criar-senha-token`,
          normalizedPayload,
        ),
      );

      if (response?.sucesso === false) {
        throw new Error(response.mensagem ?? 'Não foi possível cadastrar a nova senha.');
      }
    } catch (error: unknown) {
      throw new Error(
        this.extractApiErrorMessage(error) ?? 'Não foi possível cadastrar a nova senha.',
      );
    }
  }

  async login(email: string, password: string): Promise<User> {
    const response = await firstValueFrom(
      this.http.post<TokenResponse | TokenErrorResponse>(`${environment.apiUrl}/token`, {
        username: email,
        password,
        manterLogado: true,
        grant_type: 'password',
      }),
    );

    if (this.isLoginRejectedResponse(response)) {
      throw new Error(response.mensagem ?? 'Falha ao autenticar usuário.');
    }

    const token = this.extractToken(response);
    if (!token) {
      throw new Error('Falha ao autenticar usuário. Token inválido.');
    }

    localStorage.setItem(TOKEN_KEY, token);

    const refreshToken = this.extractRefreshToken(response);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    const structureId = this.extractStructureId(response);
    if (structureId !== null) {
      localStorage.setItem(STRUCTURE_ID_KEY, String(structureId));
    } else {
      localStorage.removeItem(STRUCTURE_ID_KEY);
    }

    const fallbackUser = this.buildUserFromToken(token) ?? {
      id: '',
      email: this.normalizeEmail(email),
      name: this.normalizeEmail(email),
    };

    this.isAuthenticatedSignal.set(true);
    this.currentUserSignal.set(fallbackUser);
    this.showTourSignal.set(true);

    return (await this.loadCurrentUser(true).catch(() => fallbackUser)) ?? fallbackUser;
  }

  async loadCurrentUser(force = false): Promise<User | null> {
    const token = this.getToken();

    if (!token) {
      this.currentUserSignal.set(null);
      return null;
    }

    const currentUser = this.currentUserSignal();
    if (!force && currentUser) {
      return currentUser;
    }

    if (!force && this.userProfileInFlight) {
      return this.userProfileInFlight;
    }

    const fallbackUser = this.buildUserFromToken(token);
    this.userProfileInFlight = this.fetchCurrentUser(fallbackUser);

    try {
      return await this.userProfileInFlight;
    } finally {
      this.userProfileInFlight = null;
    }
  }

  getToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    return this.isTokenValid(token) ? token : null;
  }

  async refreshAccessToken(): Promise<string | null> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.runRefreshFlow();

    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  dismissTour(): void {
    this.showTourSignal.set(false);
  }

  logout(): void {
    this.userProfileInFlight = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(STRUCTURE_ID_KEY);
    this.isAuthenticatedSignal.set(false);
    this.currentUserSignal.set(null);
    this.showTourSignal.set(false);
  }

  async checkAuth(): Promise<boolean> {
    const token = this.getToken();

    if (!token) {
      this.logout();
      return false;
    }

    this.isAuthenticatedSignal.set(true);
    await this.loadCurrentUser().catch(() => null);
    return true;
  }

  private isLoginRejectedResponse(
    response: TokenResponse | TokenErrorResponse,
  ): response is TokenErrorResponse {
    return response.sucesso === false;
  }

  private async runRefreshFlow(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    const structureId = this.getStructureId();

    if (!refreshToken || structureId === null) {
      return null;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<TokenResponse | TokenErrorResponse>(`${environment.apiUrl}/token`, {
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          IdEstrutura: structureId,
          manterLogado: true,
        }),
      );

      if (this.isLoginRejectedResponse(response)) {
        return null;
      }

      const token = this.extractToken(response);
      if (!token) {
        return null;
      }

      localStorage.setItem(TOKEN_KEY, token);

      const nextRefreshToken = this.extractRefreshToken(response);
      if (nextRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, nextRefreshToken);
      }

      const nextStructureId = this.extractStructureId(response);
      if (nextStructureId !== null) {
        localStorage.setItem(STRUCTURE_ID_KEY, String(nextStructureId));
      }

      this.isAuthenticatedSignal.set(true);
      return token;
    } catch {
      return null;
    }
  }

  private extractToken(response: TokenResponse | TokenErrorResponse): string | null {
    if (!('access_token' in response)) {
      return null;
    }

    return this.isTokenValid(response.access_token) ? response.access_token : null;
  }

  private extractRefreshToken(response: TokenResponse | TokenErrorResponse): string | null {
    if (!('refresh_token' in response)) {
      return null;
    }

    return this.isTokenValid(response.refresh_token) ? response.refresh_token : null;
  }

  private extractStructureId(response: TokenResponse | TokenErrorResponse): number | null {
    if ('estruturas' in response && Array.isArray(response.estruturas)) {
      const favorite = response.estruturas.find(
        (item) => item.favorito === 1 && this.isStructureIdValid(item.idEstrutura),
      );
      if (favorite?.idEstrutura !== undefined) {
        return favorite.idEstrutura;
      }

      const first = response.estruturas.find((item) => this.isStructureIdValid(item.idEstrutura));
      if (first?.idEstrutura !== undefined) {
        return first.idEstrutura;
      }
    }

    if (
      'idEstruturaPortalAgencia' in response &&
      this.isStructureIdValid(response.idEstruturaPortalAgencia)
    ) {
      return response.idEstruturaPortalAgencia;
    }

    return null;
  }

  private getRefreshToken(): string | null {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return this.isTokenValid(refreshToken) ? refreshToken : null;
  }

  private getStructureId(): number | null {
    const structureId = localStorage.getItem(STRUCTURE_ID_KEY);
    const parsedStructureId = Number(structureId);
    return this.isStructureIdValid(parsedStructureId) ? parsedStructureId : null;
  }

  private async fetchCurrentUser(fallbackUser: User | null): Promise<User | null> {
    if (fallbackUser) {
      this.currentUserSignal.set(fallbackUser);
    }

    try {
      const response = await firstValueFrom(
        this.http.get<UserProfileResponse>(`${environment.apiUrl}/token/obter-dados-usuario`),
      );

      const user = this.extractUserFromProfileResponse(response) ?? fallbackUser;
      if (!user) {
        throw new Error('Não foi possível identificar o usuário autenticado.');
      }

      this.currentUserSignal.set(user);
      return user;
    } catch (error: unknown) {
      if (fallbackUser) {
        this.currentUserSignal.set(fallbackUser);
        return fallbackUser;
      }

      throw new Error(
        this.extractApiErrorMessage(error) ?? 'Não foi possível carregar os dados do usuário.',
      );
    }
  }

  private buildUserFromToken(token: string): User | null {
    const payload = this.parseTokenPayload(token);
    return this.extractUserFromProfileResponse(payload);
  }

  private parseTokenPayload(token: string): UserProfileResponse | null {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }

    try {
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(
        Math.ceil(normalizedPayload.length / 4) * 4,
        '=',
      );
      const binaryPayload = atob(paddedPayload);
      const decodedPayload = new TextDecoder().decode(
        Uint8Array.from(binaryPayload, (character) => character.charCodeAt(0)),
      );
      const parsedPayload: unknown = JSON.parse(decodedPayload);

      return this.isRecord(parsedPayload) ? (parsedPayload as UserProfileResponse) : null;
    } catch {
      return null;
    }
  }

  private extractUserFromProfileResponse(response: unknown): User | null {
    const directUser = this.mapUserRecord(response);
    if (directUser) {
      return directUser;
    }

    if (!this.isRecord(response)) {
      return null;
    }

    const nestedCandidates = [
      response['dados'],
      response['data'],
      response['usuario'],
      response['result'],
    ];

    for (const candidate of nestedCandidates) {
      const nestedUser = this.mapUserRecord(candidate);
      if (nestedUser) {
        return nestedUser;
      }
    }

    return null;
  }

  private mapUserRecord(value: unknown): User | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const id = this.readFirstString(value, [
      'CODIGO_USUARIO',
      'codigoUsuario',
      'codigo_usuario',
      'id',
      'Id',
    ]);
    const email = this.readFirstString(value, ['EMAIL', 'email']);
    const name = this.readFirstString(value, ['NOME', 'nome', 'name']);
    const cpf = this.readFirstString(value, ['CPF', 'cpf']);
    const phone = this.readFirstString(value, ['TELEFONE', 'telefone', 'phone']);
    const birthDate = this.readFirstString(value, [
      'DATANASCIMENTO',
      'dataNascimento',
      'birthDate',
    ]);

    if (!id && !email && !name) {
      return null;
    }

    return {
      id: id ?? '',
      email: email ?? '',
      name: name ?? email ?? 'Usuário EVNT',
      cpf: cpf ?? undefined,
      phone: phone ?? undefined,
      birthDate: birthDate ?? undefined,
    };
  }

  private readFirstString(value: Record<string, unknown>, keys: readonly string[]): string | null {
    for (const key of keys) {
      const currentValue = value[key];

      if (typeof currentValue === 'string') {
        const normalizedValue = currentValue.trim();
        if (normalizedValue.length > 0) {
          return normalizedValue;
        }
      }

      if (typeof currentValue === 'number' && Number.isFinite(currentValue)) {
        return String(currentValue);
      }
    }

    return null;
  }

  private isStructureIdValid(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value > 0;
  }

  private isTokenValid(token: unknown): token is string {
    if (typeof token !== 'string') {
      return false;
    }

    const normalized = token.trim().toLowerCase();
    return normalized.length > 0 && normalized !== 'undefined' && normalized !== 'null';
  }

  private extractApiErrorMessage(error: unknown): string | null {
    if (!this.isRecord(error)) {
      return null;
    }

    const directMessage = this.readMensagem(error);
    if (directMessage) {
      return directMessage;
    }

    const nestedError = error['error'];
    if (!this.isRecord(nestedError)) {
      return null;
    }

    return this.readMensagem(nestedError);
  }

  private readMensagem(value: Record<string, unknown>): string | null {
    const mensagem = value['mensagem'];
    if (typeof mensagem !== 'string') {
      return null;
    }

    const normalizedMessage = mensagem.trim();
    return normalizedMessage.length > 0 ? normalizedMessage : null;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private normalizeEmail(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private normalizeTokenKey(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private normalizePassword(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }
}
